import { Link } from 'react-router-dom'
import type { BoardDeal } from '../../api/adapters/dealCard'
import { useSetArchived } from '../../api/queries/dealMutations'
import { userFacingMessage } from '../../api/errors'
import { DecisionButtons } from '../deals/DecisionButtons'
import { PRIORITY_LABEL, RECOMMENDATION_LABEL, RISK_LABEL, SIGNAL_LABEL } from './labels'
import styles from './DealCard.module.css'

/**
 * A deal card. Every value shown is a field the backend returned; where a value
 * is absent the card says so rather than substituting one.
 */
export function DealCard({ deal }: { deal: BoardDeal }) {
  const stageClass = deal.stage ? styles[`stage_${deal.stage}`] : styles.stage_none

  return (
    <article className={`${styles.card} ${stageClass ?? ''}`} aria-label={deal.companyName}>
      <header className={styles.top}>
        <h3 className={styles.name}>
          <Link className={styles.link} to={`/deals/${deal.id}`}>
            {deal.companyName}
          </Link>
        </h3>
        {deal.priority ? (
          <span className={`${styles.priority} ${styles[`priority_${deal.priority}`]}`}>
            {PRIORITY_LABEL[deal.priority]}
          </span>
        ) : (
          <span className={styles.unassessed}>Not yet assessed</span>
        )}
      </header>

      <dl className={styles.verdict}>
        <dt>{deal.lastDecision ? 'Last decision' : 'Recommendation'}</dt>
        <dd>
          {deal.lastDecision
            ? RECOMMENDATION_LABEL[deal.lastDecision]
            : deal.recommendation
              ? RECOMMENDATION_LABEL[deal.recommendation]
              : 'Not yet assessed'}
        </dd>
      </dl>

      {deal.reEvaluationRequired ? (
        <p className={styles.reEval}>
          <span className={styles.reEvalDot} aria-hidden="true" />
          Re-evaluation needed
        </p>
      ) : null}

      <ul className={styles.signals}>
        {deal.confirmedQualification.map((key) => (
          <li key={key} className={`${styles.signal} ${styles.confirmed}`}>
            <span className={styles.check} aria-hidden="true">✓</span>
            {SIGNAL_LABEL[key]}
          </li>
        ))}
        {deal.confirmedProgression.map((key) => (
          <li key={key} className={`${styles.signal} ${styles.progression}`} title="Deal progression">
            <span className={styles.check} aria-hidden="true">✓</span>
            {SIGNAL_LABEL[key]}
          </li>
        ))}
        {deal.missingQualification.map((key) => (
          <li key={key} className={`${styles.signal} ${styles.missing}`}>{SIGNAL_LABEL[key]}</li>
        ))}
      </ul>

      {deal.activeRisks.length > 0 ? (
        <ul className={styles.risks}>
          {deal.activeRisks.map((key) => (
            <li key={key} className={styles.risk}>
              <span className={styles.riskDot} aria-hidden="true" />
              {RISK_LABEL[key]}
            </li>
          ))}
        </ul>
      ) : null}

      {deal.archived ? (
        <div className={styles.archiveRow}>
          <span className={styles.archivedBadge}>Archived</span>
          <ArchiveToggle deal={deal} />
        </div>
      ) : (
        <div className={styles.callSection}>
          {/* The verdict above is the product's reading; this is the user's, and
              the label is what keeps the two from being read as one control. */}
          <span className={styles.callLabel}>Your call</span>
          <DecisionButtons dealId={deal.id} current={deal.lastDecision} variant="card" />
          {/* Offered where the old board offers it: once you have decided to stop,
              archiving is the next thing you want. */}
          {deal.lastDecision === 'stop_investing' ? (
            <div className={styles.archiveRow}>
              <ArchiveToggle deal={deal} />
            </div>
          ) : null}
        </div>
      )}
    </article>
  )
}

function ArchiveToggle({ deal }: { deal: BoardDeal }) {
  const archive = useSetArchived(deal.id)
  return (
    <>
      <button
        type="button"
        className={deal.archived ? styles.restore : styles.archive}
        disabled={archive.isPending}
        onClick={() => archive.mutate(!deal.archived)}
      >
        {archive.isPending ? 'Working…' : deal.archived ? 'Restore' : 'Archive'}
      </button>
      {archive.isError ? (
        <p className={styles.actionFailed} role="alert">
          {userFacingMessage(archive.error)}
        </p>
      ) : null}
    </>
  )
}
